import React, { useEffect, useRef } from 'react';
import anime from 'animejs/lib/anime.es.js';
import { skills } from '../data/mock';
import { useTheme } from '../contexts/ThemeContext';
import { TrendingUp, Zap, Star } from 'lucide-react';

const Skills = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const skillsRef = useRef(null);
  const { currentColors } = useTheme();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Enhanced title animation with 3D rotation
            anime({
              targets: titleRef.current,
              opacity: [0, 1],
              translateY: [80, 0],
              rotateX: [90, 0],
              scale: [0.8, 1.1, 1],
              duration: 1200,
              easing: 'easeOutElastic(1, .6)'
            });

            // Skills cards with advanced stagger and 3D effects
            anime({
              targets: skillsRef.current.children,
              opacity: [0, 1],
              translateY: [60, 0],
              rotateY: [180, 0],
              scale: [0.7, 1.1, 1],
              duration: 1000,
              delay: anime.stagger(200, { start: 400 }),
              easing: 'easeOutElastic(1, .8)'
            });

            // Animate progress bars with glow effects
            setTimeout(() => {
              skills.forEach((skill, index) => {
                const progressBar = document.querySelector(`.progress-bar-${index}`);
                if (progressBar) {
                  anime({
                    targets: progressBar,
                    width: [`0%`, `${skill.level}%`],
                    duration: 1500,
                    delay: index * 250,
                    easing: 'easeOutQuart'
                  });

                  // Add pulsing glow effect
                  anime({
                    targets: progressBar,
                    boxShadow: [
                      `0 0 0px ${currentColors.accent}`,
                      `0 0 15px ${currentColors.accent}80`,
                      `0 0 0px ${currentColors.accent}`
                    ],
                    duration: 2000,
                    delay: 1000 + (index * 250),
                    loop: true,
                    easing: 'easeInOutSine'
                  });
                }
              });
            }, 800);

            // Add hover animations for skill cards
            Array.from(skillsRef.current.children).forEach((card) => {
              card.addEventListener('mouseenter', () => {
                anime({
                  targets: card,
                  scale: [1, 1.05],
                  rotateY: [0, 5],
                  duration: 300,
                  easing: 'easeOutQuart'
                });
              });
              
              card.addEventListener('mouseleave', () => {
                anime({
                  targets: card,
                  scale: [1.05, 1],
                  rotateY: [5, 0],
                  duration: 300,
                  easing: 'easeOutQuart'
                });
              });
            });

            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [currentColors.accent]);

  return (
    <section 
      id="skills" 
      ref={sectionRef} 
      className="py-16 md:py-32 relative z-10 transition-colors duration-500"
      style={{ backgroundColor: currentColors.bg }}
    >
      <div className="container mx-auto px-4 md:px-6">
        <h2 
          ref={titleRef}
          className="text-4xl md:text-6xl font-bold uppercase tracking-tight text-center mb-20 opacity-0 relative font-display"
          style={{ 
            color: currentColors.text
          }}
        >
          Technical Skills
          <div 
            className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 h-1 w-32 opacity-80"
            style={{ backgroundColor: currentColors.accent }}
          />
        </h2>

        <div ref={skillsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto">
          {skills.map((skill, index) => (
            <div 
              key={index}
              className="p-8 rounded-xl transition-all duration-500 opacity-0 group cursor-pointer relative overflow-hidden"
              style={{ 
                backgroundColor: currentColors.cardBg,
                border: `2px solid ${currentColors.borderLight}`,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}
            >
              {/* Skill Header with Icon */}
              <div className="flex items-center justify-between mb-6">
                <h3 
                  className="font-mono text-sm font-bold uppercase tracking-wide"
                  style={{ color: currentColors.text }}
                >
                  {skill.category}
                </h3>
                <div className="p-2 rounded-full" style={{ backgroundColor: `${currentColors.accent}20` }}>
                  <TrendingUp size={20} style={{ color: currentColors.accent }} />
                </div>
              </div>
                
              {/* Progress Bar Container */}
              <div className="mb-6">
                <div 
                  className="w-full h-2 rounded-full relative overflow-hidden"
                  style={{ backgroundColor: currentColors.borderLight }}
                >
                  <div 
                    className={`progress-bar-${index} absolute top-0 left-0 h-full rounded-full transition-all duration-300`}
                    style={{ 
                      backgroundColor: currentColors.accent,
                      width: '0%'
                    }}
                  />
                </div>
                
                <div className="flex justify-between items-center mt-3">
                  <span 
                    className="font-mono text-xs uppercase tracking-wide"
                    style={{ color: currentColors.textSecondary }}
                  >
                    Proficiency
                  </span>
                  <div className="flex items-center gap-1">
                    <Star size={12} style={{ color: currentColors.accent }} />
                    <span 
                      className="font-mono text-sm font-bold"
                      style={{ color: currentColors.accent }}
                    >
                      {skill.level}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Technologies List */}
              <div className="space-y-3">
                {skill.technologies.map((tech, techIndex) => (
                  <div 
                    key={techIndex}
                    className="tech-item flex items-center justify-between py-2 border-b transition-all duration-300 hover:translate-x-2 cursor-pointer"
                    style={{ borderColor: `${currentColors.borderLight}80` }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = currentColors.accent;
                      anime({
                        targets: e.currentTarget,
                        translateX: [0, 8],
                        duration: 300,
                        easing: 'easeOutQuart'
                      });
                      anime({
                        targets: e.currentTarget.querySelector('.tech-dot'),
                        scale: [1, 1.5],
                        duration: 300,
                        easing: 'easeOutElastic(1, .6)'
                      });
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = `${currentColors.borderLight}80`;
                      anime({
                        targets: e.currentTarget,
                        translateX: [8, 0],
                        duration: 300,
                        easing: 'easeOutQuart'
                      });
                      anime({
                        targets: e.currentTarget.querySelector('.tech-dot'),
                        scale: [1.5, 1],
                        duration: 300,
                        easing: 'easeOutElastic(1, .6)'
                      });
                    }}
                  >
                    <span 
                      className="text-sm font-medium"
                      style={{ color: currentColors.text }}
                    >
                      {tech}
                    </span>
                    <div className="flex items-center gap-2">
                      <div 
                        className="tech-dot w-2 h-2 rounded-full opacity-70 group-hover:opacity-100 transition-opacity"
                        style={{ backgroundColor: currentColors.accent }}
                      />
                      <Zap size={12} style={{ color: currentColors.accent }} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                ))}
              </div>

              {/* Category Badge */}
              <div className="mt-6 pt-4 border-t" style={{ borderColor: `${currentColors.borderLight}50` }}>
                <div 
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono uppercase tracking-wide font-semibold transition-all duration-300 group-hover:scale-105"
                  style={{ 
                    backgroundColor: `${currentColors.accent}15`,
                    color: currentColors.accent
                  }}
                >
                  <Zap size={10} />
                  {skill.category.includes('Data') ? 'Analytics Focus' : 
                   skill.category.includes('Web') ? 'Frontend/Backend' :
                   skill.category.includes('Cross') ? 'Mobile Apps' :
                   skill.category.includes('Backend') ? 'Systems & DB' :
                   'Tools & Platforms'}
                </div>
              </div>

              {/* Hover Glow Effect */}
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl"
                style={{ 
                  background: `linear-gradient(135deg, ${currentColors.accent}05, transparent)`
                }}
              />
            </div>
          ))}
        </div>

        {/* Enhanced Skills Summary */}
        <div className="text-center mt-20">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Zap size={24} style={{ color: currentColors.accent }} />
              <h3 
                className="font-mono text-lg uppercase tracking-wide font-bold"
                style={{ color: currentColors.text }}
              >
                Continuous Innovation
              </h3>
              <Zap size={24} style={{ color: currentColors.accent }} />
            </div>
            <p 
              className="text-lg leading-relaxed"
              style={{ color: currentColors.textSecondary }}
            >
              Continuously expanding my technical expertise across the full development stack, 
              with a focus on performance optimization, scalable architecture, and data-driven solutions. 
              Every project is an opportunity to push boundaries and deliver exceptional results.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;